

import { useEffect } from "react";
import { useSettings } from "@/context/SettingsContext";

export function DynamicManifest() {
  const { settings } = useSettings();
  const { businessName, tagline } = settings.business;
  const { primaryColor } = settings.theme;
  const logoUrl = settings.logoUrl; // Use separate logoUrl from settings
  
  // PWA settings from general settings
  const pwaSettings = settings.pwa || {};
  const pwaName = pwaSettings.appName || businessName || "Shalimar ERP";
  const pwaShortName = pwaSettings.shortName || (businessName ? businessName.split(' ')[0] : "Shalimar");
  const pwaDescription = pwaSettings.description || tagline || "Premium Event Management ERP System";
  // Prefer PWA icons over main logo
  const pwaIcon192 = pwaSettings.icon192 || logoUrl;
  const pwaIcon512 = pwaSettings.icon512 || logoUrl;

  useEffect(() => {
    // The PWA manifest is served as a STATIC file (/manifest.json, linked in index.html)
    // so it loads from 'self' and satisfies the Content-Security-Policy. We must NOT build
    // a blob: OR data: manifest at runtime — both are blocked by `default-src 'self'`
    // (the fallback for `manifest-src`). Here we only update in-page branding bits
    // (manifest link presence, theme-color, title, app-name), which is always CSP-safe.

    // Ensure the static manifest link exists (in case it was stripped from the document).
    let manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
    if (!manifestLink) {
      manifestLink = document.createElement('link');
      manifestLink.rel = 'manifest';
      document.head.appendChild(manifestLink);
    }
    if (manifestLink.getAttribute('href') !== '/manifest.json') {
      manifestLink.href = '/manifest.json';
    }

    // Also update theme-color meta tag
    const themeColorMeta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement;
    if (themeColorMeta) {
      themeColorMeta.content = primaryColor || "#C9A84C";
    }

    // Update the page title
    if (pwaName) {
      document.title = `${pwaName} - ${pwaDescription || 'Premium Event Management'}`;
    }

    // Update apple-mobile-web-app-title
    const appleTitleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]') as HTMLMetaElement;
    if (appleTitleMeta) {
      appleTitleMeta.content = pwaShortName;
    }

    // Update application-name
    const appNameMeta = document.querySelector('meta[name="application-name"]') as HTMLMetaElement;
    if (appNameMeta) {
      appNameMeta.content = pwaShortName;
    }

    return () => {};
  }, [pwaName, pwaShortName, pwaDescription, primaryColor, pwaIcon192, pwaIcon512]);

  return null;
}
